import {Button} from 'primereact/button'
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import {useParams} from 'react-router-dom'
import {ReactComponent as BiBody} from '../../assets/icons/BiBody.svg'
import {ReactComponent as FaWeight} from '../../assets/icons/FaWeight.svg'
import {ReactComponent as GiBodyHeight} from '../../assets/icons/GiBodyHeight.svg'
import {useAppConfig} from '../../context/app/AppContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {useAuth} from '../authentication/AuthProvider'
import AuditLogButton from '../generic-log/AuditLogButton'
import GenericLogTable from '../generic-log/GenericLogTable'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {permissionOptionKeys} from '../role/RoleConstant'
import {useRoleContext} from '../role/RoleProvider'
import {usePatientContext} from '../styled-component/information/StyleDetailInformationWrapper'
import {tableEvent} from '../table-utils/TableUtils'
import BiometricsForm from './BiometricsForm'
import {useDetailBiometricByUserId} from './query-keys/BiometricsQueryManager'

const QueryKey = 'Biometrics'

const fontSize = 'text-xs sm:text-xs md:text-md lg:text-lg'

const Biometrics = forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
    }))

    //#region Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {myRolePermissions} = useRoleContext()
    const {handleCheckPermissionByKey} = usePatientContext()
    //#endregion

    //#region Language
    const {translateCommons} = TranslateUtils()
    //#endregion

    //#region ID
    const {id: idString} = useParams()
    const id = useMemo(() => {
        if (!idString) {
            return null
        }

        return parseInt(idString)
    }, [idString])
    //#endregion

    //#region Query
    const {data, refetch} = useDetailBiometricByUserId(user?.id, id)
    //#endregion

    //#region Event Listener
    useEffect(() => {
        if (!!id) {
            tableEvent.on('RELOAD-DATA-TABLE-' + QueryKey + id, refetch)
            return () => {
                tableEvent.removeListener('RELOAD-DATA-TABLE-' + QueryKey + id)
            }
        }
    }, [id, refetch])
    //#endregion

    //#region Ref
    const formRef = useRef(null)
    const genericRef = useRef(null)
    //#endregion

    //#region Handle Function
    const handleShowForm = data => {
        formRef.current.showForm(!!data ? {...data} : {})
    }
    //#endregion

    //#region Memo
    const bmiStatus = useMemo(() => {
        let value = data ? data.bmi : 0
        if (value && value > 0) {
            if (value < 18.5) {
                return {
                    label: 'Thin',
                    background: 'rgb(244, 174, 149)',
                    color: 'var(--gray-800)',
                }
            } else if (18.5 <= value && value <= 22.9) {
                return {
                    label: 'Average',
                    background: '#44cc61',
                    color: 'white',
                }
            } else if (23.1 <= value && value <= 24.9) {
                return {
                    label: 'Over Weight',
                    background: '#fed070',
                    color: 'var(--gray-800)',
                }
            } else if (25 <= value && value <= 29.9) {
                return {
                    label: 'Obesity I',
                    background: '#fed070',
                    color: 'var(--gray-800)',
                }
            } else if (30 <= value && value <= 30.9) {
                return {
                    label: 'Obesity II',
                    background: 'rgb(244, 174, 149)',
                    color: 'var(--gray-800)',
                }
            } else if (30.9 <= value && value <= 40) {
                return {
                    label: 'Obesity III',
                    background: '#e74142',
                    color: 'white',
                }
            } else {
                return {
                    label: 'Obesity III +',
                    background: 'rgb(207, 66, 66)',
                    color: 'white',
                }
            }
        } else {
            return {
                label: '',
                background: '',
            }
        }
    }, [data])
    const bmiData = [
        {
            key: 'height',
            label: data && data?.type === 'inchPound' ? 'In' : 'Cm',
            value: data && data?.height,
            icon: <GiBodyHeight style={{width: '20px', height: '20px'}} />,
        },
        {
            key: 'weight',
            label: data && data?.type === 'inchPound' ? 'lbs' : 'Kg',
            value: data && data?.weight,
            icon: <FaWeight style={{width: '18px', height: '18px'}} />,
        },
        {
            key: 'bmi',
            label: 'BMI',
            value: data && data?.bmi,
        },
        {
            key: 'status',
            label: '',
            value: data && data?.bmi ? translateCommons(bmiStatus.label) : '',
            className: 'col-12 md:col-3 border-top-1 md:border-none',
        },
    ]
    //#endregion

    return (
        <div>
            <div className="col-12 p-0 mb-3">
                <div className="flex border-round justify-content-between align-items-center py-3 px-2 flex-wrap bg-teal-50 mb-3 w-full">
                    <h5
                        className={`pl-2 text-darkcyan font-bold m-0 py-2 text-center flex align-items-center md:text-left`}>
                        <span className="mr-3">
                            <BiBody
                                className="font-bold"
                                style={{width: '20px', height: '20px'}}
                            />
                        </span>
                        {translateCommons('Biometrics')}
                    </h5>

                    <div className="flex gap-2 align-items-center">
                        {handleCheckPermissionByKey(
                            permissionOptionKeys.allowToAddBiometrics,
                        ) && (
                            <Button
                                className={`p-button-text audit-button gap-1 ${fontSize}`}
                                icon="pi pi-plus"
                                label={`${translateCommons('Add Biometrics')}`}
                                onClick={() =>
                                    formRef.current.showForm(
                                        !!data ? {...data} : {},
                                    )
                                }
                            />
                        )}
                        <AuditLogButton
                            label={translateCommons(
                                "Show Biometric's Audit Log",
                            )}
                            onClick={() => {
                                if (!!id && !!data?.id) {
                                    genericRef.current.showForm({
                                        refId: data?.id,
                                        type: 'biometricsLog',
                                    })
                                } else {
                                    showNotification(
                                        severityNotification.warn,
                                        'No data found !!',
                                    )
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className="flex col-12 p-0">
                <div
                    className="flex flex-wrap col-12 font-bold border-round clearfix p-0"
                    style={{
                        backgroundColor:
                            data && data?.bmi
                                ? bmiStatus.background
                                : 'var(--gray-100)',
                        color:
                            data && data?.bmi
                                ? bmiStatus.color
                                : 'var(--gray-700)',
                        border: '1px solid var(--gray-100)',
                    }}>
                    {bmiData.map((item, idx) => (
                        <div
                            key={idx}
                            className={`flex align-item-center p-3 ${
                                item.className ?? 'col-4 md:col-3'
                            }`}>
                            {item.value ? (
                                <div
                                    className={`mb-0 flex align-items-center justify-content-center ${fontSize} w-full`}>
                                    {item?.icon}
                                    <span className="ml-2">
                                        {` ${item.value} ${translateCommons(
                                            item.label,
                                        )}`}{' '}
                                    </span>
                                </div>
                            ) : (
                                translateCommons('No Data')
                            )}
                        </div>
                    ))}
                </div>
            </div>
            <BiometricsForm ref={formRef} />
            <GenericLogTable ref={genericRef} />
        </div>
    )
})

export default Biometrics
